import React, { useEffect, useRef, useState } from 'react';
import { useRoomContext } from '@livekit/components-react';

const EMPTY_METRICS = {
  kbps: 0,
  rttMs: 0,
  jitterMs: 0,
  packetsLost: 0,
  framesDropped: 0,
  tracks: 0,
};

const diagnosticsEnabled = () => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('diagnostics') === '1';
};

const participantPublications = (participant) => (
  participant?.trackPublications
    ? Array.from(participant.trackPublications.values())
    : []
);

const StreamDiagnostics = ({ label = 'Stream' }) => {
  const room = useRoomContext();
  const enabled = diagnosticsEnabled();
  const [metrics, setMetrics] = useState(EMPTY_METRICS);
  const previousSampleRef = useRef({ bytes: 0, at: 0 });

  useEffect(() => {
    if (!enabled || !room) return undefined;
    let cancelled = false;

    const sample = async () => {
      const participants = [
        room.localParticipant,
        ...Array.from(room.remoteParticipants.values()),
      ];
      const tracks = participants
        .flatMap(participantPublications)
        .map((publication) => publication?.track)
        .filter((track) => typeof track?.getRTCStatsReport === 'function');

      const reports = await Promise.all(tracks.map(async (track) => {
        try {
          return await track.getRTCStatsReport();
        } catch (_error) {
          return null;
        }
      }));

      let bytes = 0;
      let rttSeconds = 0;
      let jitterSeconds = 0;
      let packetsLost = 0;
      let framesDropped = 0;
      reports.forEach((report) => report?.forEach?.((stat) => {
        if ((stat.type === 'inbound-rtp' || stat.type === 'outbound-rtp') && !stat.isRemote) {
          bytes += Number(stat.bytesReceived || stat.bytesSent || 0);
          packetsLost += Math.max(0, Number(stat.packetsLost || 0));
          framesDropped += Math.max(0, Number(stat.framesDropped || 0));
          jitterSeconds = Math.max(jitterSeconds, Number(stat.jitter || 0));
        }
        if (stat.type === 'remote-inbound-rtp') {
          rttSeconds = Math.max(rttSeconds, Number(stat.roundTripTime || 0));
          packetsLost += Math.max(0, Number(stat.packetsLost || 0));
        }
        if (stat.type === 'candidate-pair' && stat.state === 'succeeded') {
          rttSeconds = Math.max(rttSeconds, Number(stat.currentRoundTripTime || 0));
        }
      }));

      const now = performance.now();
      const previous = previousSampleRef.current;
      const elapsedSeconds = Math.max(0.001, (now - previous.at) / 1000);
      const kbps = previous.at > 0
        ? Math.max(0, Math.round(((bytes - previous.bytes) * 8) / elapsedSeconds / 1000))
        : 0;
      previousSampleRef.current = { bytes, at: now };
      if (!cancelled) {
        setMetrics({
          kbps,
          rttMs: Math.round(rttSeconds * 1000),
          jitterMs: Math.round(jitterSeconds * 1000),
          packetsLost,
          framesDropped,
          tracks: tracks.length,
        });
      }
    };

    sample().catch(() => {});
    const interval = window.setInterval(() => sample().catch(() => {}), 3000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [enabled, room]);

  if (!enabled) return null;
  return (
    <aside className="pointer-events-none fixed right-3 top-3 z-[10000] rounded-lg border border-blue-400/40 bg-black/80 px-3 py-2 font-mono text-[10px] leading-4 text-blue-100 shadow-lg backdrop-blur-sm">
      <div className="mb-1 font-semibold text-blue-300">{label} diagnostics</div>
      <div>{metrics.kbps} kbps · {metrics.rttMs} ms RTT · {metrics.jitterMs} ms jitter</div>
      <div>{metrics.tracks} tracks · {metrics.packetsLost} lost · {metrics.framesDropped} dropped</div>
    </aside>
  );
};

export default StreamDiagnostics;
